import { useCheckIsSupportedOffice } from '@/features/transaction_items/List/Table/hooks/useCheckIsSupportedOffice';
import { useCustomTransactionTwoFlag } from '@/hooks/feature_flag/custom-transaction-two';
import { useTranslation } from '@/i18n';
import {
  ButtonV2,
  Caption,
  Tooltip,
} from '@moneyforward/ap-frontend-components';
import classNames from 'classnames';
import { FC, memo } from 'react';
import styles from './TableAction.module.scss';

const cx = classNames.bind(styles);

export type Props = {
  isDisabled?: boolean;
  customTransactionItemsCount?: number;
  onCreate?: () => void;
  onReordering?: () => void;
  isReorderingMode?: boolean;
  onCancelOrdering?: () => void;
  isLoading?: boolean;
  onSubmitReorder?: () => void;
  isSubmitDisabled?: boolean;
};

export const MAX_CUSTOM_TRANSACTION_ITEMS_COUNT = 15;

export const TableAction: FC<Props> = memo(
  ({
    isDisabled = false,
    isLoading = false,
    customTransactionItemsCount = 0,
    onCreate,
    onReordering,
    isReorderingMode,
    onCancelOrdering,
    onSubmitReorder,
    isSubmitDisabled = false,
  }) => {
    const { t } = useTranslation();

    const isCustomTransactionTwoFlag = useCustomTransactionTwoFlag();
    const isSupportedOffice = useCheckIsSupportedOffice();

    const isAddButtonVisible = isCustomTransactionTwoFlag && isSupportedOffice;

    return (
      <div className={cx(styles['table-action-container'])}>
        {!isReorderingMode &&
          isCustomTransactionTwoFlag &&
          isSupportedOffice && (
            <span>
              {t('transaction_items.table.custom_transaction_items_count', {
                count: customTransactionItemsCount,
                total: MAX_CUSTOM_TRANSACTION_ITEMS_COUNT,
              })}
            </span>
          )}
        {isReorderingMode && (
          <Caption type='helper'>
            {t(
              'transaction_items.table.custom_transaction_items.reorder.helper'
            )}
          </Caption>
        )}
        <div className={cx(styles['button-container'])}>
          {isReorderingMode ? (
            <>
              <ButtonV2 onClick={onCancelOrdering} isSecondary size='sm'>
                {t('transaction_items.reordering.cancel')}
              </ButtonV2>
              <ButtonV2
                size='sm'
                onClick={onSubmitReorder}
                isDisabled={isSubmitDisabled}
              >
                {t('transaction_items.reordering.save_ordering_setting')}
              </ButtonV2>
            </>
          ) : (
            <>
              <ButtonV2
                onClick={onReordering}
                isSecondary
                size='sm'
                isDisabled={isLoading}
              >
                {t('transaction_items.table.reordering')}
              </ButtonV2>
              {isAddButtonVisible && (
                <Tooltip
                  placement='topRight'
                  title={isDisabled && t('transaction_items.table.add.tooltip')}
                >
                  <ButtonV2
                    isDisabled={isDisabled}
                    isSecondary
                    onClick={onCreate}
                    size='sm'
                    isLoading={isLoading && !isDisabled}
                  >
                    {t('transaction_items.table.add_custom_transaction_items')}
                  </ButtonV2>
                </Tooltip>
              )}
            </>
          )}
        </div>
      </div>
    );
  }
);
TableAction.displayName = 'TableAction';
